import { createUseStyles } from "react-jss";
import { colors } from "../../styles/colors";

type Props = {
  isSearchable?: boolean;
  showPagination?: boolean;
  isBackgroundUnique?: boolean;
  isTitleHeader?: boolean;
};

const useStyles = createUseStyles((_) => ({
  container: {
    borderRadius: "15px",
    padding: "20px",
    backgroundColor: colors.surface,
    border: `1px solid ${colors.borderLight}`,
    boxShadow: "0px 1px 4px 0px rgba(0, 0, 0, 0.10)",
    // Flex column so the scrollable table area flexes and the pagination
    // footer stays pinned at the bottom of the card (always reachable).
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
    "@media (max-width:599.95px)": { padding: "14px", borderRadius: "12px" },
    "& .MuiTableContainer-root .MuiTable-root": {
      "& .MuiTableHead-root": {
        "& .MuiTableCell-root": {
          height: "40px",
          padding: "0px 10px",
          textTransform: "uppercase",
          textWrap: "nowrap",
        },
      },
      "& .MuiTableBody-root": {
        "& .MuiTableCell-root": {
          height: "40px",
          padding: "0px 10px",
          border: 0,
          textWrap: "nowrap",
        },
        "& .MuiTableRow-root:nth-child(even)": {
          backgroundColor: (props: Props) =>
            props.isBackgroundUnique ? colors.surface : colors.surfaceElevated,
        },
      },
    },
  },
  formControl: {
    "&.MuiFormControl-root": {
      maxWidth: "100%",
      // The inline `sx={{ width: "27ch" }}` is fine on desktop but overflows a
      // phone once a title sits next to it.
      "@media (max-width:599.95px)": { width: "100%" },
      "& input": {
        padding: "8px",
        paddingLeft: "16px",
        "&::placeholder": {
          color: colors.BLACK,
          fontFamily: "Poppins-Bold",
          fontSize: "10px",
        },
      },
      "& fieldset": {
        border: "none",
      },
      "& .MuiInputBase-root.MuiOutlinedInput-root": {
        fontFamily: "Poppins-Regular",
        backgroundColor: colors.default,
        borderRadius: "50px",
        fontSize: "11px",
      },
    },
  },
  contratTitle: {
    color: colors.primary,
  },
  headerContainer: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "10px",
    justifyContent: (props: Props) =>
      props.isTitleHeader ? "space-between" : "flex-end",
    marginBottom: "9px",
  },
  footerContainer: {
    marginTop: "13px",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "8px",
    // Never let the footer be squeezed out or scrolled away.
    flexShrink: 0,
    flexDirection: (props: Props) =>
      props.showPagination ? "row-reverse" : "row",
    justifyContent: "space-between",
    /*
     * The pager was collapsing into a vertical column of single buttons.
     *
     * TablePagination lays its toolbar out on one line: rows-per-page label,
     * selector, "1-25 of 47", then the actions. On a narrow card the actions
     * are what gets squeezed, and MUI's Pagination list wraps — so each page
     * button ended up on its own row, stacked down the side of the card.
     *
     * Letting the toolbar itself wrap gives the pager a full-width line of its
     * own instead of a few leftover pixels.
     */
    "& .MuiTablePagination-root": {
      minWidth: 0,
      overflow: "hidden",
    },
    "& .MuiTablePagination-toolbar": {
      flexWrap: "wrap",
      rowGap: "4px",
      minHeight: "auto",
      paddingLeft: 0,
    },
    "& .MuiTablePagination-spacer": { display: "none" },
    "& .MuiTablePagination-actions": {
      marginLeft: "auto",
      "@media (max-width:599.95px)": {
        marginLeft: 0,
        width: "100%",
      },
    },
    "& .MuiPagination-root": {
      "@media (max-width:599.95px)": { marginLeft: "0 !important" },
    },
    "& .MuiPagination-ul": {
      flexWrap: "nowrap",
      "@media (max-width:599.95px)": { justifyContent: "center" },
    },
  },
  containerTable: {
    overflowX: "auto",
    overflowY: "auto",
    maxWidth: "100%",
    // Take the remaining vertical space inside the card so the footer below
    // stays visible; the table body scrolls here instead of the whole page.
    flex: "1 1 auto",
    minHeight: 0,
    WebkitOverflowScrolling: "touch",
    /*
     * Below the breakpoint the table takes its natural height and the PAGE
     * scrolls instead.
     *
     * `flex: 1 1 auto; min-height: 0` is what lets this area scroll internally
     * on desktop, but it is also permission to shrink to nothing. When a page
     * puts tall content above the card inside a fixed-height column — a filter
     * bar whose controls stack on a narrow screen, say — the leftover space
     * goes negative and this collapses to zero. The header and footer do not
     * shrink, so the result is a card showing a search box and a pagination
     * row with no table between them, on data that loaded perfectly well.
     *
     * Capped so a long list still cannot push the pagination controls off the
     * bottom of a phone screen.
     */
    "@media (max-width:899.95px)": {
      flex: "0 0 auto",
      minHeight: "auto",
      maxHeight: "65vh",
    },
  },
  iconSort: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    cursor: "pointer",
  },
  componentHeader: {
    marginLeft: "10px",
    marginRight: "10px",
  },
  divSearchAndHeader: {
    display: "flex",
    flexDirection: "row-reverse",
    alignItems: "center",
  },
}));

export default useStyles;
